import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Card from '@elementor/ui/Card';
import CardContent from '@elementor/ui/CardContent';
import Box from '@elementor/ui/Box';
import Typography from '@elementor/ui/Typography';
import Link from '@elementor/ui/Link';
import Divider from '@elementor/ui/Divider';
import IconButton from '@elementor/ui/IconButton';
import { AIIcon, ThumbUpIcon, ThumbsDownIcon } from '@elementor/icons';
import RelatedMedia from './RelatedMedia';
import { buildAnswerContext, pushDataLayerEvent, submitFeedback } from '../helpers';
import { FEATURES } from '../config';

/**
 * AISummaryCard — owns the answer markdown, related videos, and the
 * thumbs-up/down feedback row.
 *
 * Props are `response` + `query` (rather than `content` + `videos`)
 * because the feedback POST needs the full response envelope:
 * `answer`, `confidence`, `answerTimestamp`, `feedbackToken`, plus the
 * citations/videos/related_articles to rebuild `answer_context`. See
 * `helpers/api.js > submitFeedback` and `feedback-token.js` (CF) for
 * the contract.
 *
 * Lock-in: a single `feedbackState` (null → "up" or "down") gates both
 * thumbs. After the first click in either direction, both buttons are
 * inert. This matches the locked design decision: one feedback event
 * per question.
 *
 * Failure modes are deliberately silent in the UI:
 *   - missing token / timestamp on the response → thumbs still render,
 *     submitFeedback is skipped, "Thanks for your feedback!" still
 *     shows (the user took an action; logging it via dataLayer is the
 *     primary signal regardless of Kafka delivery).
 *   - submitFeedback rejected (network / 403 / TOKEN_EXPIRED) → logged
 *     in console, UI does not change.
 */
function AISummaryCard({ response, query }) {
  const [feedbackState, setFeedbackState] = useState(null);

  const content = response?.answer || '';
  const confidence = String(response?.confidence || '').toUpperCase();
  const answerTimestamp = response?.answerTimestamp;
  const feedbackToken = response?.feedbackToken;

  const videos = useMemo(
    () => (response?.videos || []).filter((v) => v?.link),
    [response],
  );

  const sendFeedback = (sentiment) => {
    if (feedbackState !== null) return;
    setFeedbackState(sentiment === 'POSITIVE' ? 'up' : 'down');

    pushDataLayerEvent({
      section: 'in page',
      element_name: 'feedback button',
      outcome: sentiment === 'POSITIVE' ? 'I like the result' : 'I dislike the result',
      search_term: query,
      additional_description: content,
    });

    // Skip the POST if the response didn't carry the HMAC envelope —
    // CF would 400 on missing fields anyway. The dataLayer event above
    // is unaffected.
    if (!answerTimestamp || !feedbackToken) {
      console.warn('Feedback skipped: response missing answerTimestamp or feedbackToken');
      return;
    }

    submitFeedback({
      question: query,
      answer: content,
      confidence,
      answerTimestamp,
      answer_context: buildAnswerContext(response),
      sentiment,
      feedbackToken,
    }).catch((err) => {
      console.error('Feedback submit unexpected error:', err);
    });
  };

  return (
    <Card
      className="ft-ai-summary"
      variant="outlined"
      sx={{
        mb: 3,
        borderRadius: 4,
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 300,
          opacity: 0.15,
          background: 'linear-gradient(180deg, rgba(237, 1, 238, 1) 0%, rgba(255, 255, 255, 1) 70%)',
          borderRadius: '9px 9px 0px 0px',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 3, position: 'relative' }}>
        <AIIcon sx={{ color: 'text.primary' }} />
        <Typography variant="subtitle1" fontWeight={700}>
          AI Summary
        </Typography>
      </Box>

      <CardContent sx={{ px: 3, position: 'relative' }}>
        <Box
          sx={{
            mb: 2,
            '& p': {
              mb: 1.5,
              lineHeight: 1.7,
            },
            '& ul, & ol': {
              pl: 2,
              mb: 1.5,
            },
            '& li': {
              mb: 0.5,
            },
            '& code': {
              backgroundColor: 'grey.100',
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: 14,
            },
            '& pre': {
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
            },
          }}
        >
          <ReactMarkdown
            components={{
              a: ({ href, children }) => (
                <Link
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  underline="hover"
                >
                  {children}
                </Link>
              ),
              code: ({ children }) => (
                <code>{children}</code>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </Box>

        <Box className="ft-ai-feedback" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            *AI can make mistakes double check results
          </Typography>
          {FEATURES.feedback && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                aria-label="Thumbs up"
                onClick={() => sendFeedback('POSITIVE')}
                disabled={feedbackState !== null}
                disableRipple
                sx={{
                  color: feedbackState === 'up' ? '#ED01EE' : 'text.secondary',
                  backgroundColor: 'transparent !important',
                  '&:hover': { color: '#ED01EE', backgroundColor: '#F6F6F6 !important' },
                  '&.Mui-disabled': {
                    color: feedbackState === 'up' ? '#ED01EE' : 'text.disabled',
                  },
                }}
              >
                <ThumbUpIcon fontSize="small" sx={feedbackState === 'up' ? { color: '#ED01EE !important' } : {}} />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Thumbs down"
                onClick={() => sendFeedback('NEGATIVE')}
                disabled={feedbackState !== null}
                disableRipple
                sx={{
                  color: feedbackState === 'down' ? '#ED01EE' : 'text.secondary',
                  backgroundColor: 'transparent !important',
                  '&:hover': { color: '#ED01EE', backgroundColor: '#F6F6F6 !important' },
                  '&.Mui-disabled': {
                    color: feedbackState === 'down' ? '#ED01EE' : 'text.disabled',
                  },
                }}
              >
                <ThumbsDownIcon fontSize="small" sx={feedbackState === 'down' ? { color: '#ED01EE !important' } : {}} />
              </IconButton>
              {feedbackState && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  Thanks for your feedback!
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {videos.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <RelatedMedia videos={videos} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default AISummaryCard;
